import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 * @property {import('@theme/component-quantity-selector').QuantitySelectorComponent} [quantitySelector] - Sticky quantity selector
 * @property {HTMLElement} [variantPanel] - Sticky variant selector panel
 * @property {HTMLElement} [variantPanelOverlay] - Sticky variant selector overlay
 * @property {HTMLButtonElement} [variantPanelClose] - Button that closes variant selector panel
 * @property {HTMLButtonElement} [variantPanelConfirm] - Button that confirms selection
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Keeps product purchase controls visible at the bottom of the page.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {number | null} */
  #panelAnimationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  connectedCallback() {
    super.connectedCallback();

    this.#setupStickyBar();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener('keydown', this.#handleKeydown, { signal });

    this.#getInitialQuantity();
    this.#syncQuantityFromMainSelector();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    document.documentElement.style.removeProperty('overflow');
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    if (this.#panelAnimationTimeout) {
      clearTimeout(this.#panelAnimationTimeout);
    }
  }

  /**
   * Sets up the sticky bar and caches the main product add-to-cart button.
   */
  #setupStickyBar() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    this.#showStickyBar();
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (this.#hasVariantPanel()) {
      this.#openVariantPanel();
      return;
    }

    await this.#addSelectedVariantToCart();
  };

  /**
   * Closes the sticky variant panel.
   */
  handleCloseVariantPanelClick = () => {
    this.#closeVariantPanel();
  };

  /**
   * Syncs an option selected in the sticky panel to the main variant picker.
   * @param {PointerEvent} event
   */
  handleStickyOptionClick = (event) => {
    const optionButton = event.target instanceof Element ? event.target.closest('[data-option-value-id]') : null;
    if (!(optionButton instanceof HTMLElement)) return;

    const optionValueId = optionButton.dataset.optionValueId;
    if (!optionValueId) return;

    const variantPicker = this.#getVariantPicker();
    if (!variantPicker) return;

    const matchingInput = variantPicker.querySelector(`input[data-option-value-id="${optionValueId}"]`);
    if (matchingInput instanceof HTMLInputElement) {
      matchingInput.click();
      this.#syncPanelValueState(optionValueId);
      return;
    }

    const matchingOption = variantPicker.querySelector(`option[data-option-value-id="${optionValueId}"]`);
    if (matchingOption instanceof HTMLOptionElement && matchingOption.parentElement instanceof HTMLSelectElement) {
      const select = matchingOption.parentElement;
      select.value = matchingOption.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      this.#syncPanelValueState(optionValueId);
    }
  };

  /**
   * Confirms the selected options and adds the currently selected variant to cart.
   */
  handleConfirmSelectionClick = async () => {
    this.#closeVariantPanel();
    await this.#addSelectedVariantToCart();
  };

  /**
   * Adds the selected variant by delegating to the main product form button.
   */
  async #addSelectedVariantToCart() {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  }

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const panelWasOpen = this.#isVariantPanelOpen();
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;
    const newVariantPanel = newStickyAddToCart.querySelector('[ref="variantPanel"]');

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });
    if (this.refs.variantPanel && newVariantPanel) {
      morph(this.refs.variantPanel, newVariantPanel, { childrenOnly: true });
    }

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;
    if (panelWasOpen) {
      this.#openVariantPanel();
    }

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    this.#syncQuantityFromMainSelector();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Handles Escape key for the sticky variant panel.
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key === 'Escape' && this.#isVariantPanelOpen()) {
      this.#closeVariantPanel();
    }
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#syncQuantitySelectors(event);
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the main variant picker for this product.
   * @returns {HTMLElement | null}
   */
  #getVariantPicker() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    return sectionElement.querySelector(`variant-picker[data-product-id="${productId}"]`);
  }

  /**
   * Checks whether this sticky bar has a variant panel.
   * @returns {boolean}
   */
  #hasVariantPanel() {
    return Boolean(this.refs.variantPanel);
  }

  /**
   * Checks whether the variant panel is open.
   * @returns {boolean}
   */
  #isVariantPanelOpen() {
    return Boolean(this.refs.variantPanel && !this.refs.variantPanel.hidden);
  }

  /**
   * Opens the sticky variant panel.
   */
  #openVariantPanel() {
    const { variantPanel, variantPanelOverlay, addToCartButton, variantPanelClose } = this.refs;
    if (!variantPanel || !variantPanelOverlay) return;

    if (this.#panelAnimationTimeout) {
      clearTimeout(this.#panelAnimationTimeout);
    }

    variantPanel.hidden = false;
    variantPanelOverlay.hidden = false;
    variantPanel.classList.add('is-opening');
    variantPanelOverlay.classList.add('is-opening');
    variantPanel.classList.remove('is-closing');
    variantPanelOverlay.classList.remove('is-closing');

    requestAnimationFrame(() => {
      variantPanel.classList.remove('is-opening');
      variantPanelOverlay.classList.remove('is-opening');
    });

    addToCartButton.setAttribute('aria-expanded', 'true');
    document.documentElement.style.overflow = 'hidden';
    const closeButton = variantPanel.querySelector('[ref="variantPanelClose"]');
    if (closeButton instanceof HTMLButtonElement) {
      closeButton.focus();
    } else {
      variantPanelClose?.focus();
    }
  }

  /**
   * Closes the sticky variant panel.
   */
  #closeVariantPanel() {
    const { variantPanel, variantPanelOverlay, addToCartButton } = this.refs;
    if (!variantPanel || !variantPanelOverlay) return;

    if (variantPanel.hidden) return;

    if (this.#panelAnimationTimeout) {
      clearTimeout(this.#panelAnimationTimeout);
    }

    variantPanel.classList.remove('is-opening');
    variantPanelOverlay.classList.remove('is-opening');
    variantPanel.classList.add('is-closing');
    variantPanelOverlay.classList.add('is-closing');
    addToCartButton.setAttribute('aria-expanded', 'false');
    document.documentElement.style.removeProperty('overflow');

    this.#panelAnimationTimeout = window.setTimeout(() => {
      variantPanel.hidden = true;
      variantPanelOverlay.hidden = true;
      variantPanel.classList.remove('is-closing');
      variantPanelOverlay.classList.remove('is-closing');
      this.#panelAnimationTimeout = null;
    }, 280);
  }

  /**
   * Marks the clicked option as selected immediately while the section update is loading.
   * @param {string} optionValueId
   */
  #syncPanelValueState(optionValueId) {
    const selectedButton = this.querySelector(`[data-option-value-id="${optionValueId}"]`);
    const optionGroup = selectedButton?.closest('.sticky-add-to-cart__panel-option');
    if (!optionGroup) return;

    optionGroup.querySelectorAll('.sticky-add-to-cart__panel-value').forEach((button) => {
      button.classList.toggle('is-selected', button === selectedButton);
    });
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Keeps the sticky quantity selector and main product quantity selector in sync.
   * @param {QuantitySelectorUpdateEvent} event
   */
  #syncQuantitySelectors(event) {
    const quantity = event.detail.quantity.toString();
    const eventTarget = event.target instanceof Element ? event.target : null;
    const cameFromSticky = eventTarget?.closest('sticky-add-to-cart') === this;
    const selectorToUpdate = cameFromSticky ? this.#getMainQuantitySelector() : this.refs.quantitySelector;

    if (!selectorToUpdate || selectorToUpdate.getValue?.() === quantity) return;

    selectorToUpdate.setValue?.(quantity);
    selectorToUpdate.updateButtonStates?.();
    if (cameFromSticky) {
      selectorToUpdate.dispatchEvent(new QuantitySelectorUpdateEvent(event.detail.quantity));
    }
  }

  /**
   * Syncs sticky quantity from the main product form after rendering or variant changes.
   */
  #syncQuantityFromMainSelector() {
    const mainQuantitySelector = this.#getMainQuantitySelector();
    const quantity = mainQuantitySelector?.getValue?.() || this.dataset.initialQuantity || '1';

    this.#currentQuantity = parseInt(quantity) || 1;
    if (this.refs.quantitySelector && this.refs.quantitySelector.getValue?.() !== quantity) {
      this.refs.quantitySelector.setValue?.(quantity);
      this.refs.quantitySelector.updateButtonStates?.();
    }
    this.#updateButtonText();
  }

  /**
   * Gets the quantity selector from the main product form.
   * @returns {import('@theme/component-quantity-selector').QuantitySelectorComponent | null}
   */
  #getMainQuantitySelector() {
    return /** @type {import('@theme/component-quantity-selector').QuantitySelectorComponent | null} */ (
      this.#getProductForm()?.querySelector('quantity-selector-component[ref="quantitySelector"]') ?? null
    );
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
